import { useMemo } from "react";
import { useGetDataPackageVersionHistoryQuery } from "~/graphql/supergraphHooks";
import { sortByVersionDesc } from "../sorting";
import {
  GenericVersionHistoryTakeover,
  type VersionData,
} from "./GenericVersionHistoryTakeover";
import { HierarchyPanel } from "./HierarchyPanel";
import type { TypeHierarchy } from "./HierarchyTreeGrid";
import type { GetDataPackageVersionHistoryQuery } from "~/graphql/supergraphTypes";

type VersionHistoryTakeoverProps = {
  arenaId: number | null;
  dataPackageId: number;
  onExit: () => void;
  onDownload: (version?: number) => void;
  isDownloading?: boolean;
};

type HierarchyVersionData = VersionData<{
  typeHierarchy: TypeHierarchy | null;
}>;
type DataPackageVersionHistoryItem =
  GetDataPackageVersionHistoryQuery["finpubDataPackageVersionHistory"][number];

export const VersionHistoryTakeover = ({
  arenaId,
  dataPackageId,
  onExit,
  onDownload,
  isDownloading,
}: VersionHistoryTakeoverProps) => {
  const { data, loading } = useGetDataPackageVersionHistoryQuery({
    variables: { arenaId, dataPackageId },
    fetchPolicy: "cache-and-network",
  });

  const versions = useMemo<HierarchyVersionData[]>(() => {
    const history: DataPackageVersionHistoryItem[] =
      data?.finpubDataPackageVersionHistory ?? [];
    return sortByVersionDesc(history).map(
      (v: DataPackageVersionHistoryItem): HierarchyVersionData => {
        return {
          id: v.id,
          version: v.version,
          createdAt: v.createdAt,
          createdBy: v.createdByUser?.fullName?.trim() || null,
          updatedAt: v.updatedAt ?? v.createdAt,
          data: {
            typeHierarchy: v.typeHierarchy,
          },
        };
      },
    );
  }, [data?.finpubDataPackageVersionHistory]);

  return (
    <GenericVersionHistoryTakeover
      title="Hierarchy version history"
      versions={versions}
      loading={loading}
      onExit={onExit}
      renderPreview={(version) =>
        version ? (
          <HierarchyPanel
            title="Hierarchy preview"
            typeHierarchy={version.data.typeHierarchy}
            onDownload={() => onDownload(version.version)}
            onUpdateHierarchy={() => {}}
            isDownloading={isDownloading}
            hideUpdateAction
          />
        ) : null
      }
    />
  );
};
